import findLast from 'lodash/findLast';
import orderBy from 'lodash/orderBy';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { BetSlipContext } from '@/components/betSlip/betSlipContext';
import { usePoolSummary } from '@/composable/useJackpotData';
import type { IDynamicI18nConfig } from '@/interface/IDynamicI18nConfig';
import { JackpotPoolType } from '@/interface/jackpot';
import { useCustomerStore } from '@/store/customerStore';

export default function useJackpotCalculator() {
    const {
        betSlip,
        stakeInput,
    } = BetSlipContext.inject();

    const { setStake, stake } = stakeInput;

    const balance = computed(() => betSlip.value.balance);

    const { poolSummary } = usePoolSummary();

    const nextPool = computed(() => {
        const list = orderBy(poolSummary.value, 'minQualifyingAmount', 'desc');

        const next = findLast(list, pool => pool.minQualifyingAmount > stake.value);

        return next;
    });

    const megaPoolMinQualifyingAmount = computed(() => {
        const megaPool = poolSummary.value.find(pool => pool.poolType === JackpotPoolType.Mega);
        return megaPool?.minQualifyingAmount ?? 0;
    });

    const isShowJackpotMessage = computed(() => poolSummary.value.length > 0
     && balance.value !== null
     && balance.value >= (nextPool.value?.minQualifyingAmount ?? megaPoolMinQualifyingAmount.value));

    const isAllQualified = computed(() => {
        if (!isShowJackpotMessage.value) {
            return false;
        }

        const megaPool = poolSummary.value.find(pool => pool.poolType === JackpotPoolType.Mega);
        return stake.value >= (megaPool?.minQualifyingAmount ?? 0);
    });

    const amountToFillUp = computed(() => {
        if (!isShowJackpotMessage.value) {
            return 0;
        }
        if (isAllQualified.value) {
            return 0;
        }
        return Math.max(0, (nextPool.value?.minQualifyingAmount ?? 0) - stake.value);
    });

    const isShowAddNowButton = computed(() => isShowJackpotMessage.value && !isAllQualified.value);

    const { formatMoneyWithSymbolAndDecimal } = useCustomerStore();
    const jackpotMessage: Ref<IDynamicI18nConfig | string> = computed(() => {
        if (!isShowJackpotMessage.value) {
            return '';
        }

        if (isAllQualified.value) {
            return {
                key: 'jackpot_bet_qualifies_message',
            };
        }

        return {
            key: 'bet_slip_notice_jackpot_qualification_info',
            slots: [
                {
                    name: 'amount',
                    content: formatMoneyWithSymbolAndDecimal(amountToFillUp.value),
                    wrapper: {
                        tag: 'strong',
                    },
                },
                {
                    name: 'type',
                    key: `jackpot_pool_${nextPool.value?.poolType.toLowerCase() ?? ''}`,
                },
            ],
        };
    });

    const fillUpToMinQualifyingAmount = () => {
        setStake(stake.value + amountToFillUp.value);
    };

    return {
        jackpotMessage,
        isShowAddNowButton,
        amountToFillUp,
        isAllQualified,
        fillUpToMinQualifyingAmount,
    };
}
